import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axiosConfig';

import { useAuth } from '../context/AuthContext';
import CounselingCountModal from './modal/CounselingCountModal';

import icon from '../assets/chatSubmit.svg';

interface Message {
  sender: 'user' | 'bot';
  content: string;
}

interface ChatInterfaceProps {
  sessionId?: string;
}

const TYPE_SPEED = 30; // ms per char (속도 조절)

const ChatInterface: React.FC<ChatInterfaceProps> = ({ sessionId }) => {
  const { user, setUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isCounselingCountModalOpen, setIsCounselingCountModalOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);

  // 타이핑 상태 관리
  const typingTimerRef = useRef<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setCurrentSessionId(sessionId);
  }, [sessionId]);

  // 채팅 히스토리 불러오기
  useEffect(() => {
    const fetchHistory = async () => {
      if (!currentSessionId) {
        setMessages([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get(`/api/counseling/${currentSessionId}`);
        setMessages(response.data.messages || []);
      } catch (error) {
        console.error('Failed to fetch chat history:', error);
      }
      setIsLoading(false);
    };
    fetchHistory();
  }, [currentSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    // 언마운트 시 타이머 정리
    return () => {
      if (typingTimerRef.current) window.clearInterval(typingTimerRef.current);
    };
  }, []);

  // 봇 타이핑 효과
  const typeOutBotMessage = (fullText: string) => {
    setMessages(prev => [...prev, { sender: 'bot', content: '' }]);
    setIsTyping(true);

    let i = 0;
    typingTimerRef.current = window.setInterval(() => {
      i++;
      const next = fullText.slice(0, i);
      setMessages(prev => {
        const copy = [...prev];
        const lastIdx = copy.length - 1;
        if (lastIdx >= 0 && copy[lastIdx].sender === 'bot') {
          copy[lastIdx] = { ...copy[lastIdx], content: next };
        }
        return copy;
      });

      if (i >= fullText.length) {
        if (typingTimerRef.current) window.clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        setIsTyping(false);
      }
    }, TYPE_SPEED);
  };

  // 메세지 전송 핸들러
  const handleSend = async () => {
    if (!user) return;

    // user.isPaidUser가 false일 경우에만 상담 횟수 감소
    if (!user.isPaidUser) {
      let newUserState = { ...user };
      if (newUserState.todayCounselingCount > 0) {
        newUserState.todayCounselingCount -= 1;
      } else if (newUserState.referredCounselingCount > 0) {
        newUserState.referredCounselingCount -= 1;
      } else {
        setIsCounselingCountModalOpen(true);
        return;
      }

      setUser(newUserState);
    }

    if (input.trim() === '' || isLoading || isTyping) return;

    const userMessage: Message = { sender: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // 상담 메시지 전송
      const response = await axios.post('/api/counseling', {
        message: userMessage.content,
        sessionId: currentSessionId,
      });

      if (!user.isPaidUser) {
        axios.put('/api/users/deduct-counseling-count', { userId: user.id }).catch(err => {
          console.error('Failed to sync counseling count:', err);
        });
      }

      // 봇 응답 처리
      const botText: string =
        response.data?.reply ??
        response.data?.botReply ??
        (() => {
          const arr: Message[] = response.data?.messages || [];
          const last = arr[arr.length - 1];
          return last && last.sender === 'bot' ? last.content : '';
        })() ??
        '';

      if (!currentSessionId && response.data?._id) {
        setCurrentSessionId(response.data._id);
      }

      if (botText) {
        typeOutBotMessage(botText);
      } else {
        setMessages(prev => [
          ...prev,
          { sender: 'bot', content: '죄송합니다, 답변을 생성하는 데 실패했습니다.' },
        ]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [
        ...prev,
        { sender: 'bot', content: '죄송합니다, 답변을 생성하는 데 실패했습니다.' },
      ]);
    }

    setIsLoading(false);
  };

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const EXPAND_THRESHOLD = 60;

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';

    if (input.length >= EXPAND_THRESHOLD) {
      el.style.height = `${el.scrollHeight}px`;
      el.style.paddingBottom = `40px`;
    } else {
      el.style.height = '40px';
      el.style.paddingBottom = `0px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [input]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return (
    <div className="chat-interface">
      <div className="message-list">
        {messages.map((msg, index) => (
          <div key={index} className={`message-bubble ${msg.sender}`}>
            <p>
              {msg.sender === 'bot' && isTyping && index === messages.length - 1
                ? msg.content + '▌'
                : msg.content}
            </p>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="message-input-area">
        <textarea
          ref={textareaRef}
          className="message-textarea"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="메시지를 입력하세요..."
          disabled={isLoading || isTyping}
          rows={1}
        />
        <button onClick={handleSend} disabled={isLoading || isTyping}>
          <img src={icon} alt="대화전송" />
        </button>
      </div>
      {isCounselingCountModalOpen && (
        <CounselingCountModal
          isOpen={isCounselingCountModalOpen}
          onClose={() => setIsCounselingCountModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatInterface;
